"use client";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { MenuIcon } from "lucide-react";
import { Link } from "next-view-transitions";
import { Fragment, useState } from "react";

import type { Menu } from "../types/payload";

import { DialogTitle } from "../shadcn/components/ui/dialog";
import { Separator } from "../shadcn/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTrigger } from "../shadcn/components/ui/sheet";
import { cn } from "../shadcn/utils";
import { Logo } from "./logo";

export function MobileNavigationBar({ className, menuData }: { className?: string; menuData: Menu }) {
	const [open, setOpen] = useState<boolean>(false);
	return (
		<div className="block lg:hidden">
			<div className={`fixed left-8 z-30 top-8 text-white ${className ?? ""}`}>
				<Link href="/">
					<Logo height="30px" width="auto" />
				</Link>
			</div>
			<Sheet key={"mobile-menu"} onOpenChange={setOpen} open={open}>
				<SheetTrigger className="fixed top-8 right-8 z-30">
					<MenuIcon
						className={cn([
							`stroke-1 size-8 text-inherit scale-x-150`,
							!open && "opacity-100 pointer-events-auto",
							open && "opacity-0 pointer-events-none",
							className,
						])}
					/>
				</SheetTrigger>
				<SheetContent
					className="bg-[#605D51] text-white border-transparent !w-full !max-w-none [&_svg]:size-8"
					side="right"
				>
					<VisuallyHidden>
						<DialogTitle>Navigation</DialogTitle>
					</VisuallyHidden>
					<SheetHeader className="flex flex-col gap-3">
						<Separator className="w-11/12 bg-white mx-auto mt-4" />
						{menuData.mobileNavigation.map((item) => (
							<Fragment key={`mobile-navigation-${item.id}-${item.label}-container`}>
								<Link
									className="uppercase tracking-wider text-lg text-left w-11/12 mx-auto"
									href={item.link}
									key={`mobile-navigation-${item.id}-${item.label}`}
									onClick={() => {
										setOpen(false);
									}}
								>
									{item.label}
								</Link>
								<Separator
									className="w-11/12 bg-white mx-auto mt-4"
									key={`mobile-navigation-${item.id}-${item.label}-separator`}
								/>
							</Fragment>
						))}
					</SheetHeader>
				</SheetContent>
			</Sheet>
		</div>
	);
}

export default function NavigationBar({ className, menuData }: { className?: string; menuData: Menu }) {
	const [open, setOpen] = useState<boolean>(false);
	return (
		<>
			<MobileNavigationBar className={className} menuData={menuData} />
			<header
				className={`hidden lg:flex fixed bg-gradient-to-b from-black/10 to-black/0 box-border top-0 font-skia tracking-wide left-0 w-full p-6 lg:p-8 text-white z-30 items-start ${className ?? ""}`}
			>
				<nav className="flex-1 flex gap-6 lg:gap-6 xl:gap-12 items-start text-sm h-full uppercase tracking-wider">
					{menuData.primaryHeader.map((item) => (
						<Link href={item.link} key={`primary-header-${item.id}`}>
							{item.label}
						</Link>
					))}
				</nav>
				<div>
					<Link href="/">
						<Logo height="42px" width="auto" />
					</Link>
				</div>
				<div className="flex-1 flex justify-end">
					<Sheet key="desktop-menu" onOpenChange={setOpen} open={open}>
						<SheetTrigger className={className}>
							<MenuIcon
								className={cn([
									`stroke-1 text-inherit scale-x-150`,
									!open && "opacity-100 pointer-events-auto",
									open && "opacity-0 pointer-events-none",
									className,
								])}
							/>
						</SheetTrigger>
						<SheetContent
							className={`!bg-transparent bg-gradient-to-l from-black/15 to-transparent !text-inherit shadow-none border-none !w-72 ${className ?? ""}`}
						>
							<VisuallyHidden>
								<DialogTitle>Navigation</DialogTitle>
							</VisuallyHidden>
							<nav className={`flex flex-col gap-4 items-end font-skia ${className ?? ""}`}>
								{menuData.sidebarNavigation.map((item) => (
									<Link
										className="text-lg tracking-wider uppercase text-inherit"
										href={item.link}
										key={`sidebar-navigation-${item.id}`}
										onClick={() => {
											setOpen(false);
										}}
									>
										{item.label}
									</Link>
								))}
							</nav>
						</SheetContent>
					</Sheet>
				</div>
			</header>
		</>
	);
}
